import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { FormikActions } from 'formik'
import {
  CreateProvingForm,
  CreateProvingFormSchema,
} from 'forms/CreateProvingForm'
import {
  useCreateProvingCommand,
  useCustomSearchCommand,
  useShowCommand,
} from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import { User, UserRole } from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { ProvingInsertType } from '../../model'
import { AssignedWorkerList } from './AssignedWorkerList'
import {
  useCreateProvingMutation,
  useReadProvingsQuery,
} from './AssignedWorkerView.generated'

export type maintenanceHeadDetails = Pick<User, 'name' | '_id'> & {
  provingGround: string
  provingId: string
  provingTitle: string
  responsible: any
  status: any
  department: string
}

const AssignedWorkerListView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()

  const [routeParams, updateRouteParams] = useRouteParams<{
    search?: string
  }>('provings', {
    search: undefined,
  })

  const [panelState, showPanel, hidePanel] =
    useFormikPanelState<ProvingInsertType>()

  const showWorker = React.useCallback(
    (worker: maintenanceHeadDetails) => {
      router.navigate('provings.details', {
        id: worker.provingId,
        page: 'participants',
      })
    },
    [router]
  )

  const [selection, selectedItems] = useSelection<maintenanceHeadDetails>({
    getKey: (item) => item!._id!,
  })

  useCreateProvingCommand(10, () => showPanel({} as ProvingInsertType))

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => showWorker(selectedItems[0]),
    },
    [selectedItems]
  )

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.proving.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => {
        updateRouteParams({ search: undefined })
      },
    },
    [routeParams, updateRouteParams]
  )

  const [createProving] = useCreateProvingMutation()

  const { loading, data, refetch, error } = useReadProvingsQuery({
    variables: {
      search: routeParams.search,
    },
  })

  const items =
    !loading && data && data.provings
      ? data.provings.filter((x) => !x._deleted)
      : []
  const workerDetailsList: maintenanceHeadDetails[] = []
  items.forEach((proving: any) => {
    if (proving.maintenanceHead) {
      proving.maintenanceHead.forEach((mh: User) => {
        const provingAndMhData = {
          ...mh,
          status: proving.status,
          provingId: proving.id,
          provingTitle: proving.title,
          provingGround: proving.provingGround
            ? proving.provingGround.title
            : '',
          responsible: proving.responsible,
          department: proving.department ? proving.department : '',
          dateFrom: proving.dateFrom,
          dateTo: proving.dateTo,
        } as maintenanceHeadDetails
        workerDetailsList.push(provingAndMhData)
      })
    }
  })

  const handleSubmit = async (
    values: ProvingInsertType,
    formikActions: FormikActions<ProvingInsertType>
  ) => {
    formikActions.setSubmitting(true)

    await createProving({
      variables: { input: values },
    })
    refetch({
      search: routeParams.search,
    })

    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    hidePanel()
  }

  return (
    <FlexSpinner loading={loading} error={error}>
      <AssignedWorkerList
        items={workerDetailsList}
        onItemInvoked={showWorker}
        selection={selection}
        alwaysCollapsed={true}
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new CreateProvingFormSchema()}
        headerText={t('model.proving.create')}
      >
        <CreateProvingForm />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const AssignedWorkerView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.ServiceTeam,
        UserRole.ProvingOrganisation,
        UserRole.Reader,
        UserRole.Maintenancehead,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <AssignedWorkerListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)
